import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ResponsiveLayouts, LayoutItem } from 'react-grid-layout';
import {
  layoutTable,
  layoutKey,
  type LayoutZone,
  type DashboardMode,
} from '@/persistence/config-db';
import {
  computeZoneLayout,
  type GridBreakpoint,
} from '@/features/dashboard/default-layouts';
import type { WidgetKind } from '@/types/widget';

type LoadedState = {
  lg: LayoutItem[] | undefined;
};

/**
 * Persist and restore react-grid-layout layouts for one dashboard zone.
 *
 * Takes the current widget ids as input: the effective layout is
 * computed by merging persisted positions (for known ids) with
 * auto-generated positions (for newly-added widgets). Stale persisted
 * items are dropped when their widget no longer exists.
 *
 * Writes are debounced to avoid hammering the DB during drag.
 */
export function usePersistedLayout(
  datasetId: string | null,
  mode: DashboardMode,
  zone: LayoutZone,
  widgetIds: readonly string[],
  extraRowsById?: Readonly<Record<string, number>>,
  widgetKindById?: Readonly<Record<string, WidgetKind>>,
): {
  layouts: ResponsiveLayouts<GridBreakpoint>;
  onLayoutChange: (
    _currentLayout: unknown,
    allLayouts: ResponsiveLayouts<GridBreakpoint>,
  ) => void;
  loaded: boolean;
} {
  const [persisted, setPersisted] = useState<LoadedState>({ lg: undefined });
  const [loaded, setLoaded] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Load persisted layout from IndexedDB when (dataset, mode) changes.
  useEffect(() => {
    if (!datasetId) {
      setPersisted({ lg: undefined });
      setLoaded(true);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const stored = await layoutTable(zone).get(layoutKey(datasetId, mode));
        if (cancelled) return;
        if (stored) {
          const parsed = JSON.parse(stored.layouts) as { lg?: LayoutItem[] };
          setPersisted({ lg: parsed.lg ? [...parsed.lg] : undefined });
        } else {
          setPersisted({ lg: undefined });
        }
      } catch {
        if (!cancelled) setPersisted({ lg: undefined });
      }
      if (!cancelled) setLoaded(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [datasetId, mode, zone]);

  // Effective layout = persisted + auto-generated for missing ids.
  const layouts = useMemo<ResponsiveLayouts<GridBreakpoint>>(() => {
    const lg = computeZoneLayout(
      zone,
      widgetIds,
      persisted.lg,
      extraRowsById,
      widgetKindById,
    );
    return { lg };
  }, [zone, widgetIds, persisted.lg, extraRowsById, widgetKindById]);

  const onLayoutChange = useCallback(
    (_currentLayout: unknown, allLayouts: ResponsiveLayouts<GridBreakpoint>) => {
      const lg: LayoutItem[] = allLayouts.lg ? [...allLayouts.lg] : [];
      setPersisted({ lg });

      if (!datasetId) return;

      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void layoutTable(zone).put({
          key: layoutKey(datasetId, mode),
          datasetId,
          mode,
          layouts: JSON.stringify({ lg }),
          updatedAt: Date.now(),
        });
      }, 300);
    },
    [datasetId, mode, zone],
  );

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  return { layouts, onLayoutChange, loaded };
}
